import { useMemo } from 'react'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormState,
  type ValidationSummaryItem,
  useAddFieldValidation,
} from '@workwave-tidal/core/form'

import { RESERVED_PASSWORD_TERMS } from '@/constants'

import { createPasswordValidator } from './createPasswordValidator'
import { useTexts } from './useTexts'

type Texts = ReturnType<typeof useTexts>

export type StandardPasswordValidationsOptions = {
  customTexts: Texts
  /** When provided, the password must not contain this username. */
  username: string
  /** Overrides the terms the password must not contain. Defaults to RESERVED_PASSWORD_TERMS. */
  reservedTerms: string[]
}

export function useStandardPasswordValidations<
  TargetField extends NarrowFieldsByValueType<string, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
>(field: TargetField, options?: Partial<StandardPasswordValidationsOptions>) {
  const defaultTexts = useTexts()

  const texts = options?.customTexts || defaultTexts
  const username = options?.username
  const reservedTerms = options?.reservedTerms || RESERVED_PASSWORD_TERMS

  const { validationSummaryItemsId, validations, validationSummaryItems } = useMemo(() => {
    const validationSummaryItemsId = {
      'too_short': `${field as string}_too_short`,
      'excludes_username': `${field as string}_excludes_username`,
      'excludes_reserved_terms': `${field as string}_excludes_reserved_terms`,
      'excludes_repeated_patterns': `${field as string}_excludes_repeated_patterns`,
    }

    const validationSummaryItems = [
      {
        ids: [validationSummaryItemsId.too_short],
        field,
        message: texts.passwordLengthValidation,
      },
      {
        ids: [validationSummaryItemsId.excludes_repeated_patterns],
        field,
        message: texts.passwordExcludesRepeatedPatternsValidation,
      },
      // the rules below are listed only when the data they compare against is available
      ...(username
        ? [
            {
              ids: [validationSummaryItemsId.excludes_username],
              field,
              message: texts.passwordExcludesUsernameValidation,
            },
          ]
        : []),
      ...(reservedTerms.length > 0
        ? [
            {
              ids: [validationSummaryItemsId.excludes_reserved_terms],
              field,
              message: texts.passwordExcludesReservedTermsValidation,
            },
          ]
        : []),
    ] as ValidationSummaryItem<State>[]

    return {
      validationSummaryItems,
      validationSummaryItemsId,
      validations: createPasswordValidator(),
    }
  }, [texts, field, username, reservedTerms])

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return !validations.validateTooShort(field.value as string)
    },
    {
      customUID: validationSummaryItemsId.too_short,
    },
  )

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return validations.validateMatchesUsername(field.value as string, username)
    },
    {
      customUID: validationSummaryItemsId.excludes_username,
    },
  )

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return validations.validateContainsReservedTerms(field.value as string, reservedTerms)
    },
    {
      customUID: validationSummaryItemsId.excludes_reserved_terms,
    },
  )

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return validations.validateRepeatedPattern(field.value as string)
    },
    {
      customUID: validationSummaryItemsId.excludes_repeated_patterns,
    },
  )

  return validationSummaryItems
}
